'use client';

import { useCallback, useState } from 'react';

import {
  SessionLimitModal,
  type SessionLimitState,
  type ActiveSession,
} from '@/features/session-management';

interface SessionLimitModalContentProps {
  state: SessionLimitState;
  onClose: () => void;
}

export function SessionLimitModalContent({
  state,
  onClose,
}: SessionLimitModalContentProps) {
  const [sessions, setSessions] = useState<ActiveSession[]>(state.activeSessions);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeRequest = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.accessToken}`,
          ...options.headers,
        },
      });
      return response.json();
    },
    [state.accessToken],
  );

  const upgradeAndRedirect = useCallback(async () => {
    const result = await makeRequest('/api/auth/upgrade-token', {
      method: 'POST',
      body: JSON.stringify({ rememberMe: state.rememberMe }),
    });

    if (result.success) {
      // After upgrade, use full page navigation to ensure cookies are available
      window.location.href = result.redirectUrl || '/';
      return true;
    }

    setError(result.error || 'Failed to continue login');
    return false;
  }, [makeRequest, state.rememberMe]);

  const terminateSelected = useCallback(
    async (sessionId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await makeRequest(`/api/auth/sessions/${sessionId}`, {
          method: 'DELETE',
        });

        if (!result.success) {
          setError(result.error || 'Failed to terminate session');
          setIsLoading(false);
          return;
        }

        setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));

        // Slot freed — proceed with login (keeps loading until redirect).
        const redirected = await upgradeAndRedirect();
        if (!redirected) {
          setIsLoading(false);
        }
      } catch {
        setError('Failed to terminate session');
        setIsLoading(false);
      }
    },
    [makeRequest, upgradeAndRedirect],
  );

  const terminateAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await makeRequest('/api/auth/sessions/all', {
        method: 'DELETE',
      });

      if (result.success) {
        setSessions([]);
      } else {
        setError(result.error || 'Failed to terminate all sessions');
      }
    } catch {
      setError('Failed to terminate all sessions');
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest]);

  const upgradeAndContinue = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const redirected = await upgradeAndRedirect();
      if (!redirected) {
        setIsLoading(false);
      }
    } catch {
      setError('Failed to continue login');
      setIsLoading(false);
    }
  }, [upgradeAndRedirect]);

  return (
    <SessionLimitModal
      sessions={sessions}
      isLoading={isLoading}
      error={error}
      onTerminateSelected={terminateSelected}
      onTerminateAll={terminateAll}
      onClose={onClose}
      onContinue={upgradeAndContinue}
    />
  );
}
